import React, { useEffect, useState } from "react";
import PhoneNumberField from "../../components/FormComponents/PhoneNumberField";
import InputField from "../../components/FormComponents/InputField";
import InputSelecter from "../../components/FormComponents/InputSelecter";
import { useDispatch } from "react-redux"; // Import hooks
import { getDropdown } from "../../Redux/Actions/UsersProfileAction"; // Import the action
import { toast } from "react-toastify";

const FamilyDetails = ({ formData, updateFormData, nextStep, prevStep,
  handlePhoneNumberChange, handleCodeChange,
}) => {

  const dispatch = useDispatch();
  const [castData, setCastData] = useState([]); // State to store dropdown data
  const [errors, setErrors] = useState({}); // State for error messages

  useEffect(() => {
    // Dispatch action to get dropdown data
    const fetchDropDown = async () => {
      try {
        const res = await dispatch(getDropdown());
        if (res?.data?.data?.cast) {
          console.log("API Response:", res.data.data.marital); // Log the data
          setCastData(res.data.data.cast);

        } else {
          console.log("Error: Cast data not found in API response");
          toast.error("Failed to fetch cast data.");
        }
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
        toast.error(error?.response?.data?.message || "Failed to fetch data.");
      }
    };

    fetchDropDown();
  }, [dispatch]); // Only dispatch once when component mounts

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Father Name validation
    if (!formData.father_name?.trim()) {
      newErrors.father_name = "Father's name is required";
    } else if (formData.father_name.length < 2) {
      newErrors.father_name = "Father's name should be at least 2 characters long";
    }

    // Father Occupation validation
    if (!formData.father_occupation?.trim()) {
      newErrors.father_occupation = "Father's occupation is required";
    } else if (formData.father_occupation.length < 2) {
      newErrors.father_occupation = "Father's occupation should be at least 2 characters long";
    }

    // Father Phone Number validation
    if (!formData.father_phone_number) {
      newErrors.father_phone_number = "Father's phone number is required";
    } else if (formData.father_phone_number.length < 10) {
      newErrors.father_phone_number = "Please enter valid father's phone number";
    }

    // Mother Name validation
    if (!formData.mother_name?.trim()) {
      newErrors.mother_name = "Mother's name is required";
    } else if (formData.mother_name.length < 2) {
      newErrors.mother_name = "Mother's name should be at least 2 characters long";
    }

    // Mother Occupation validation (optional but validate if provided)
    if (formData.mother_occupation && formData.mother_occupation.length < 2) {
      newErrors.mother_occupation = "Mother's occupation should be at least 2 characters long";
    }

    // Mother Phone Number validation (optional but validate if provided)
    if (formData.mother_phone_number && formData.mother_phone_number.length < 10) {
      newErrors.mother_phone_number = "Please enter valid mother's phone number";
    }

    // Parents Native validation
    if (!formData.parents_native?.trim()) {
      newErrors.parents_native = "Parents native place is required";
    } else if (formData.parents_native.length < 2) {
      newErrors.parents_native = "Parents native place should be at least 2 characters long";
    }

    // Maternal Uncle Name validation
    if (!formData.maternal_uncle_name?.trim()) {
      newErrors.maternal_uncle_name = "Maternal uncle's name is required";
    } else if (formData.maternal_uncle_name.length < 2) {
      newErrors.maternal_uncle_name = "Maternal uncle's name should be at least 2 characters long";
    }

    // Maternal Uncle Native Place validation
    if (!formData.maternal_uncle_native_place?.trim()) {
      newErrors.maternal_uncle_native_place = "Maternal uncle's native place is required";
    } else if (formData.maternal_uncle_native_place.length < 2) {
      newErrors.maternal_uncle_native_place = "Maternal uncle's native place should be at least 2 characters long";
    }

    return newErrors;
  };

  const handleNext = (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = validateForm();
    setErrors(newErrors);

    // Check if there are any errors
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the errors before proceeding");
      return;
    }
    
    nextStep();
  };

  // Helper function to update form data and clear errors
  const handleUpdateFormData = (e) => {
    updateFormData(e);
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors(prev => ({ ...prev, [e.target.name]: "" }));
    }
  };

  // Helper function for phone number changes with error clearing
  const handlePhoneChangeWithValidation = (field, newPhone) => {
    handlePhoneNumberChange(field, newPhone);
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Helper function for country code changes with error clearing
  const handleCodeChangeWithValidation = (field, newCode) => {
    handleCodeChange(field, newCode);
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto p-4">
      <div className="max-h-[70vh] lg:max-h-[70vh] overflow-y-auto px-2">
        {/* Father Details */}
        <label className="block font-medium mb-2">Father<span className="text-[14px] text-[#E72848]">*</span></label>
        <div className="my-2">
          <InputField
            label="Name"
            name="father_name"
            placeholder="Enter father's name"
            value={formData.father_name}
            onChange={handleUpdateFormData}
            error={errors.father_name}
            required={true}
          />
          {errors.father_name && <p className="text-red-500 text-sm mt-1">{errors.father_name}</p>}
        </div>
        <div className="my-2">
          <InputField
            label="Occupation"
            name="father_occupation"
            placeholder="Enter father's occupation"
            value={formData.father_occupation}
            onChange={handleUpdateFormData}
            error={errors.father_occupation}
            required={true}
          />
          {errors.father_occupation && <p className="text-red-500 text-sm mt-1">{errors.father_occupation}</p>}
        </div>
        <div className="mb-4">
          <div className="mui-tel-input">
            <label className="block my-1 font-medium text-md ">Phone Number<span className="text-[14px] text-[#E72848]">*</span></label>
            <PhoneNumberField
              value={formData.father_phone_number}
              setPhoneNumber={(newPhone) =>
                handlePhoneChangeWithValidation("father_phone_number", newPhone)
              }
              disabled={false}
              code={formData.father_country_code}
              setCode={(newCode) =>
                handleCodeChangeWithValidation("father_country_code", newCode)
              }
              country={formData.father_country}
              error={errors.father_phone_number}
            />
            {errors.father_phone_number && <p className="text-red-500 text-sm mt-1">{errors.father_phone_number}</p>}
          </div>
        </div>

        {/* Mother Details */}
        <label className="block font-medium mb-2">Mother<span className="text-[14px] text-[#E72848]">*</span></label>
        <div className="my-2">
          <InputField
            label="Name"
            name="mother_name"
            placeholder="Enter mother's name"
            value={formData.mother_name}
            onChange={handleUpdateFormData}
            error={errors.mother_name}
            required={true}
          />
          {errors.mother_name && <p className="text-red-500 text-sm mt-1">{errors.mother_name}</p>}
        </div>
        <div className="my-2">
          <InputField
            label="Occupation"
            name="mother_occupation"
            placeholder="Enter mother's occupation"
            value={formData.mother_occupation}
            onChange={handleUpdateFormData}
            required={false}
            error={errors.mother_occupation}
          />
          {errors.mother_occupation && <p className="text-red-500 text-sm mt-1">{errors.mother_occupation}</p>}
        </div>
        <div className="mb-4">
          <div className="mui-tel-input">
            <label className="block my-1 font-medium text-md ">Phone Number</label>
            <PhoneNumberField
              value={formData.mother_phone_number}
              setPhoneNumber={(newPhone) =>
                handlePhoneChangeWithValidation("mother_phone_number", newPhone)
              }
              disabled={false}
              code={formData.mother_country_code}
              setCode={(newCode) =>
                handleCodeChangeWithValidation("mother_country_code", newCode)
              }
              country={formData.mother_country}
              error={errors.mother_phone_number}
            />
            {errors.mother_phone_number && <p className="text-red-500 text-sm mt-1">{errors.mother_phone_number}</p>}
          </div>
        </div>

        <div className="my-2">
          <InputField
            label="parents Native"
            name="parents_native"
            placeholder="Enter Parent's Native place"
            value={formData.parents_native}
            onChange={handleUpdateFormData}
            error={errors.parents_native}
            required={true}
          />
          {errors.parents_native && <p className="text-red-500 text-sm mt-1">{errors.parents_native}</p>}
        </div>

        {/* Commented out parents cast field */}
        {/* <div className="my-2">
          <InputSelecter
            label="parents Cast"
            name="parents_cast"
            value={formData.parents_cast}
            onChange={updateFormData}
            options={castData && castData.map((item) => ({
              label: item.text,
              value: item.text
            }))} // Map the data correctly to create options
          />
        </div> */}

        {/* Maternal Uncle Details */}
        <label className="block font-medium mb-2">Maternal Uncle<span className="text-[14px] text-[#E72848]">*</span></label>
        <div className="my-2">
          <InputField
            label="Name"
            name="maternal_uncle_name"
            placeholder="Enter uncle's name"
            value={formData.maternal_uncle_name}
            onChange={handleUpdateFormData}
            required={true}
            error={errors.maternal_uncle_name}
          />
          {errors.maternal_uncle_name && <p className="text-red-500 text-sm mt-1">{errors.maternal_uncle_name}</p>}
        </div>
        
        {/* Commented out maternal uncle occupation field */}
        {/* <div className="my-2">
          <InputField
            label="Occupation"
            name="maternal_uncle_occupation"
            placeholder="Enter uncle's occupation"
            value={formData.maternal_uncle_occupation}
            onChange={updateFormData}
            required={false}
          />
        </div> */}
        
        {/* Commented out maternal uncle phone number field */}
        {/* <div className="mb-4">
          <div className="mui-tel-input">
            <label className="block my-1 font-medium text-md ">Phone Number</label>
            <PhoneNumberField
              value={formData.maternal_phone_number}
              setPhoneNumber={(newPhone) =>
                handlePhoneNumberChange("maternal_phone_number", newPhone)
              }
              disabled={false}
              code={formData.maternal_country_code}
              setCode={(newCode) =>
                handleCodeChange("maternal_country_code", newCode)
              }
              country={formData.maternal_country}
            />
          </div>
        </div> */}
        
        <div className="my-2">
          <InputField
            label="Native Place/Village in India"
            name="maternal_uncle_native_place"
            placeholder="Enter place"
            value={formData.maternal_uncle_native_place}
            onChange={handleUpdateFormData}
            error={errors.maternal_uncle_native_place}
            required={true}
          />
          {errors.maternal_uncle_native_place && <p className="text-red-500 text-sm mt-1">{errors.maternal_uncle_native_place}</p>}
        </div>
      </div>
      <div className="flex justify-center gap-3 mt-6 w-full px-2">
        <button
          type="button"
          className="flex-1 py-3 text-[#E72848] border border-[#E72848] h-[45px] rounded-xl font-semibold hover:bg-[#FCE4EC] transition duration-300 cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            prevStep();
          }}
        >
          Back
        </button>
        <button
          type="button"
          className="flex-1 py-3 bg-[#E72848] text-white rounded-xl h-[45px] font-semibold hover:bg-[#f53b58] transition duration-300 cursor-pointer"
          onClick={handleNext}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default FamilyDetails;